import { AlertTriangle, HelpCircle, X } from "lucide-react";
import { useState } from "react";
import type { NodeDef } from "../../types/flows";
import { accentOf, NodeIcon } from "./ui";

// The node registry is the only description of a node that exists, so this
// renders it wherever a node appears: the palette tooltip, the canvas `?`
// popover, the config panel header and the template gallery's chains. There is
// nothing to keep in sync because there is no second copy of the text.

export const HelpBody = ({ def }: { def: NodeDef }) => (
	<div className="space-y-3 text-[12px] leading-relaxed text-slate-600">
		<p className="text-[13px] font-medium text-slate-800">{def.help.summary}</p>
		<p>{def.help.details}</p>
		<div className="grid grid-cols-2 gap-3">
			<div>
				<p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
					Takes
				</p>
				<ul className="space-y-0.5">
					{def.help.inputs.length === 0 && (
						<li className="text-slate-400">Nothing</li>
					)}
					{def.help.inputs.map((line) => (
						<li key={line}>{line}</li>
					))}
				</ul>
			</div>
			<div>
				<p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
					Gives
				</p>
				<ul className="space-y-0.5">
					{def.help.outputs.length === 0 && (
						<li className="text-slate-400">Nothing</li>
					)}
					{def.help.outputs.map((line) => (
						<li key={line}>{line}</li>
					))}
				</ul>
			</div>
		</div>
		{def.help.example && (
			<div>
				<p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
					For example
				</p>
				<p className="rounded-lg bg-slate-50 px-2.5 py-1.5 font-mono text-[11px] text-slate-600">
					{def.help.example}
				</p>
			</div>
		)}
		{def.help.gotchas && def.help.gotchas.length > 0 && (
			<div>
				<p className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-amber-600">
					<AlertTriangle className="h-3 w-3" /> Watch out
				</p>
				<ul className="list-disc space-y-1 pl-4 text-slate-600">
					{def.help.gotchas.map((line) => (
						<li key={line}>{line}</li>
					))}
				</ul>
			</div>
		)}
		{(def.sideEffect || def.runIfEmpty) && (
			<p className="border-t border-slate-100 pt-2 text-[11px] text-slate-400">
				{def.sideEffect &&
					"Reaches outside PriceOnline, so it is skipped on empty input and honours dry run. "}
				{def.runIfEmpty && "Runs even with nothing coming in."}
			</p>
		)}
	</div>
);

/** The `?` on a canvas node and in the config panel header. */
export const HelpPopover = ({
	def,
	accent,
}: {
	def: NodeDef;
	accent?: string;
}) => {
	const [open, setOpen] = useState(false);
	const tone = accentOf(accent);

	return (
		<span className="relative">
			<button
				type="button"
				aria-label={`What ${def.label} does`}
				onClick={(event) => {
					event.stopPropagation();
					setOpen((value) => !value);
				}}
				className="rounded-full p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
			>
				<HelpCircle className="h-3.5 w-3.5" />
			</button>
			{open && (
				<div
					className="absolute right-0 top-6 z-50 w-80 rounded-xl border border-slate-200 bg-white p-4 shadow-xl"
					onClick={(event) => event.stopPropagation()}
					onKeyDown={(event) => event.stopPropagation()}
					role="dialog"
				>
					<div className="mb-3 flex items-center gap-2">
						<span className={`rounded-md p-1 ${tone.bg} ${tone.text}`}>
							<NodeIcon name={def.icon} className="h-3.5 w-3.5" />
						</span>
						<span className="text-[13px] font-semibold text-slate-800">
							{def.label}
						</span>
						<button
							type="button"
							onClick={() => setOpen(false)}
							className="ml-auto rounded p-0.5 text-slate-400 hover:bg-slate-100"
							aria-label="Close"
						>
							<X className="h-3.5 w-3.5" />
						</button>
					</div>
					<HelpBody def={def} />
				</div>
			)}
		</span>
	);
};

/** The hover card the palette shows: summary plus what goes in and out. */
export const HelpTooltip = ({ def }: { def: NodeDef }) => (
	<div className="pointer-events-none absolute left-full top-0 z-50 ml-2 hidden w-64 rounded-xl border border-slate-200 bg-white p-3 text-[11px] leading-relaxed text-slate-600 shadow-xl group-hover:block">
		<p className="mb-1.5 text-[12px] font-medium text-slate-800">
			{def.help.summary}
		</p>
		<p>
			<span className="font-semibold text-slate-400">In: </span>
			{def.help.inputs.join(", ") || "nothing"}
		</p>
		<p>
			<span className="font-semibold text-slate-400">Out: </span>
			{def.help.outputs.join(", ") || "nothing"}
		</p>
	</div>
);
